import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { GuideItem, ReferralGuideForm, ReferralGuideSpecialty } from '@project/view-models';
import { ApiService, IPagingResponse } from '@core/http';
import { environment } from '@env';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpResponse } from '@angular/common/http';

export type GuideType = 'Apac' | 'Sadt' | 'Referral';

export interface GuideDto {
  id: string;
  type: GuideType;
  created_at: string;
  doctor_specialization?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SessionReferralGuideService {
  constructor(private apiService: ApiService, private http: HttpClient) {}

  getReferralGuidesviaPatientId(
    patientId: string,
    page: number,
    itemsPerPage: number,
  ): Observable<IPagingResponse<GuideItem>> {
    const offset = (page - 1) * itemsPerPage;

    return this.http
      .get(`${environment.environmentVariables.apiCoreUrl}/ReferralForm`, {
        observe: 'response',
        responseType: 'text',
        withCredentials: true,
        params: {
          PatientId: patientId,
          limit: itemsPerPage,
          offset,
          orderBy: 'created_at',
          direction: 'desc',
        } as any,
      })
      .pipe(map((resp) => this.httpToPagingResponse(resp, itemsPerPage)));
  }

  getReferralGuidesviaRequisitionId(
    requisitionId: string,
    page: number,
    itemsPerPage: number,
  ): Observable<IPagingResponse<GuideItem>> {
    const offset = (page - 1) * itemsPerPage;

    return this.http
      .get(`${environment.environmentVariables.apiCoreUrl}/ReferralForm`, {
        observe: 'response',
        responseType: 'text',
        withCredentials: true,
        params: {
          RequisitionId: requisitionId,
          limit: itemsPerPage,
          offset,
          orderBy: 'created_at',
          direction: 'desc',
        } as any,
      })
      .pipe(map((resp) => this.httpToPagingResponse(resp, itemsPerPage)));
  }

  getGuidesviaRequisitionId(
    requisitionId: string,
    page: number,
    itemsPerPage: number,
    type?: GuideType,
  ): Observable<IPagingResponse<GuideDto>> {
    const offset = (page - 1) * itemsPerPage;

    return this.http
      .get(`${environment.environmentVariables.apiCoreUrl}/Guide`, {
        observe: 'response',
        responseType: 'text',
        withCredentials: true,
        params: {
          requisitionId,
          limit: itemsPerPage,
          offset,
          ...(type && { type }),
        } as any,
      })
      .pipe(map((resp) => this.httpToPagingResponse(resp, itemsPerPage)));
  }

  getGuidesviaPatientId(
    patientId: string,
    page: number,
    itemsPerPage: number,
    type?: GuideType,
  ): Observable<IPagingResponse<GuideDto>> {
    const offset = (page - 1) * itemsPerPage;

    return this.http
      .get(`${environment.environmentVariables.apiCoreUrl}/Guide`, {
        observe: 'response',
        responseType: 'text',
        withCredentials: true,
        params: {
          patientId,
          limit: itemsPerPage,
          offset,
          ...(type && { type }),
        } as any,
      })
      .pipe(map((resp) => this.httpToPagingResponse(resp, itemsPerPage)));
  }

  createReferralGuideForm(ReferralGuideForm: ReferralGuideForm): Observable<void> {
    return this.apiService.post<void>(`${environment.environmentVariables.apiCoreUrl}/ReferralForm`, ReferralGuideForm);
  }

  getAvailableSpecialtiesForMedicalReferral(): Observable<ReferralGuideSpecialty[]> {
    return this.apiService.get<ReferralGuideSpecialty[]>(`${environment.environmentVariables.apiCoreUrl}/Specialty`);
  }

  private httpToPagingResponse(resp: HttpResponse<string>, itemsPerPage: number): IPagingResponse<GuideDto> {
    const total = Number(resp.headers.get('PTM-Total-Count') ?? 0);

    const raw = resp.body
      ? (() => {
          try {
            return JSON.parse(resp.body);
          } catch {
            return [];
          }
        })()
      : [];

    const items: GuideDto[] = Array.isArray(raw) ? raw : Array.isArray((raw as any)?.items) ? (raw as any).items : [];

    return {
      items,
      totalItemsCount: total,
      requestTotalItemsCount: total,
      itemsPerPage,
      requestTotalPages: itemsPerPage > 0 ? Math.ceil(total / itemsPerPage) : 0,
    };
  }

  downloadGuide(id: string): Observable<string> {
    return this.apiService
      .get(`${environment.environmentVariables.apiCoreUrl}/ReferralForm/${id}/Download`, {}, 'text')
      .pipe(
        map((response: any) => {
          return response.toString();
        }),
        catchError((error) => {
          return throwError(() => new Error(error));
        }),
      );
  }
}
